import cn from 'classnames';
import { FC, useMemo } from 'react';

import { Bars } from '@/Components/Partials/Charts';
import { Filter3, FilterData } from '@/Components/UI';
import { isNumber } from '@/Lib';
import { analyticsStoreSelectors } from '@/Store';
import { ChartDatasetValues, StatisticBestProduct, StatisticBrand, WhiffTotalsValue } from '@/Types/analytics';

import { AnalyticChartCardInfo } from '../../AnalyticChartCardInfo';
import { AnalyticChartFilterInfo } from '../../AnalyticChartFilterInfo';
import { BAR_FACTOR, MINI_TOP_STEP_VALUE, TOP_STEP_VALUE } from '../FavoriteHeartChart/constants';
import S from './styles.module.scss';

type PrintoutCountsPerItemChartProps = {
    data: ChartDatasetValues;
    brands: StatisticBrand[];
    bestProduct?: StatisticBestProduct;
    totalPrintouts: WhiffTotalsValue;
    mini?: boolean;
};

export const PrintoutCountsPerItemChart: FC<PrintoutCountsPerItemChartProps> = ({ data, brands, bestProduct, totalPrintouts, mini = false }) => {
    const filterPrintoutByBrand = analyticsStoreSelectors.use.printoutFilterByBrand();

    const stepValue = mini ? MINI_TOP_STEP_VALUE : TOP_STEP_VALUE;

    const bestProductDescription = useMemo(
        () => (
            <>
                <p>{bestProduct?.name}</p>
                <p>most popular per period</p>
            </>
        ),
        [bestProduct]
    );

    const onChangeBrandFilter = (item: FilterData) => {
        const brandId = item.brand_id ? Number(item.brand_id) : null;

        if (!brandId) {
            filterPrintoutByBrand(null);
        }

        if (isNumber(brandId) && !isNaN(brandId)) {
            filterPrintoutByBrand(brandId);
        }
    };

    const filters = [
        <Filter3
            key={`brands-${brands.map((b) => b.id).join('-')}`}
            className={S.info_filters}
            onChange={onChangeBrandFilter}
            fromData={{
                brands,
            }}
            isSaveInQuery={false}
            resetOptionName="All brands"
        >
            <Filter3.Brands visibleLabel={''} visibleName="All brands" />
        </Filter3>,
    ];

    const cardsInfo = useMemo(() => {
        const items: JSX.Element[] = [];

        if (bestProduct) {
            items.push(<AnalyticChartCardInfo key={bestProduct.value} title={bestProduct.value.toString()} description={bestProductDescription} />);
        }

        items.push(<AnalyticChartCardInfo key="total-printouts" title={totalPrintouts.toString()} description="Total completed operation" />);

        return items;
    }, [bestProduct, bestProductDescription, totalPrintouts]);

    const height = data.length * stepValue * BAR_FACTOR;

    return (
        <div className={cn(S.whiffs_chart, { [S['whiffs_chart-mini']]: mini })}>
            <h3 className={cn(S.title, { [S['title-mini']]: mini })}>Printout counts per item (per specific custom brand)</h3>

            {!mini && <AnalyticChartFilterInfo filters={filters} cardsInfo={cardsInfo} />}

            <Bars data={data} height={height} isHorizontal axisDataName="Product name" isHideFractionalTicks mini={mini} variant="printoutCounts" />
        </div>
    );
};
